/**
 * @file ManifestNormalizer.ts
 * @description 将外部 DeviceManifest 归一化为 SDK 内部使用的通道模型。
 * manager、runtime、devices 后续都应读取 channels，而不是直接理解 update/updateWCH/pairing。
 */

import type { ChannelDescriptor } from '../channels/Channel'
import type { DeviceManifest } from '../types/manifest'
import type { NormalizedDeviceManifest } from './ManifestTypes'
import { registerDeviceManifests } from './DeviceManifestRegistry'

/**
 * 归一化单个 manifest。
 */
export function normalizeManifest(manifest: DeviceManifest): NormalizedDeviceManifest {
  const channels = normalizeManifestChannels(manifest)
  return {
    ...manifest,
    channels,
  }
}

/**
 * 归一化 manifest 列表。
 */
export function normalizeManifests(manifests: DeviceManifest[]): NormalizedDeviceManifest[] {
  const normalizedManifests = manifests.map(normalizeManifest)
  // 关键：底座子鼠标不是 manager 扫描出来的物理设备，只能通过这里的索引按 VID/PID 找驱动。
  registerDeviceManifests(normalizedManifests)
  return normalizedManifests
}

/**
 * 从 manifest filters 派生语义化 channel 描述。
 */
export function normalizeManifestChannels(manifest: DeviceManifest): ChannelDescriptor[] {
  const filters = manifest.hardware.filters ?? []

  if (filters.length === 0) {
    return [
      {
        kind: manifest.hardware.transport === 'BLE' ? 'ble' : 'hid',
        usageType: 'default',
      },
    ]
  }

  return filters.map((filter) => {
    return {
      kind: manifest.hardware.transport === 'BLE' ? 'ble' : 'hid',
      usageType: filter.type ?? 'default',
      usagePage: filter.usagePage,
      usage: filter.usage,
      description: filter.description,
      vendorId: filter.vendorId,
      productId: filter.productId,
      productName: filter.productName,
    }
  })
}
